import { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { skipToken } from '@reduxjs/toolkit/query'
import { RootState } from 'store'
import { useSendInvoiceEmailMutation } from 'store/invoices/invoices.api'
import { clearInvoiceEmail } from 'store/invoices/invoices.slice'
import { InvoiceResponse } from 'store/invoices/invoices.types'
import { Order } from 'store/orders/orders.types'
import {
  useGetCompanyByIdQuery,
  useGetCompanyEmailsQuery,
  useGetUserCompanyAssociationByUserQuery,
} from 'store/company/company.api'
import { useGetUserByIdQuery } from 'store/auth/auth.api'

export const useSendInvoiceEmailHooks = (
  invoice: InvoiceResponse,
  order: Order | undefined,
  handleClose: () => void
) => {
  const dispatch = useDispatch()
  const [errorOnSending, setErrorOnSending] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const invoices = useSelector((state: RootState) => state.invoices)

  const [sendInvoiceEmail, { isLoading, isError }] =
    useSendInvoiceEmailMutation()

  const { data: company } = useGetCompanyByIdQuery(
    order?.companyId
      ? { id: order.companyId }
      : skipToken
  )
  const { data: companyEmails } = useGetCompanyEmailsQuery(
    order?.companyId
      ? { companyId: order.companyId }
      : skipToken
  )

  const { data: userCompanyAssociations } = useGetUserCompanyAssociationByUserQuery(
    invoice?.userId
      ? { userId: invoice.userId }
      : skipToken
  )

  const { data: user } = useGetUserByIdQuery(
    invoice?.userId
      ? { userId: invoice.userId }
      : skipToken
  )

  const toEmail = useMemo(
    () => {
      const latestInvoiceEmailFromTable = companyEmails?.find(
        (email) => email.emailType === 'InvoicePodEmail'
      )?.email
      return (
        latestInvoiceEmailFromTable ??
        company?.invoicePodEmail ??
        company?.accountPayableEmail ??
        company?.notificationEmail
      )
    },
    [
      companyEmails,
      company?.invoicePodEmail,
      company?.accountPayableEmail,
      company?.notificationEmail,
    ]
  )

  // User email is added by backend automatically, show it as read-only
  const userEmail = useMemo(() => {
    if (user?.email && user.email.trim() !== '') {
      return user.email.trim()
    }
    return null
  }, [user?.email])

  const factoringEmails = useMemo(() => {
    if (!invoice?.userId || !invoice?.customerId || !userCompanyAssociations) {
      return []
    }

    // Find the association that matches the invoice customerId and has factoring enabled
    const association = userCompanyAssociations.find(
      (assoc) => assoc.companyId === invoice.customerId && assoc.factoringEnabled
    )

    // If factoring is enabled, return the transpots accounting email
    if (association) {
      return ['accounting@transpots.com']
    }

    return []
  }, [invoice?.userId, invoice?.customerId, userCompanyAssociations])

  const handleSendInvoice = async () => {
    try {
      await sendInvoiceEmail({
        invoiceId: invoice.id,
        recipient: invoices.invoiceEmailTo,
        subject: invoices.invoiceEmailSubject,
        message: invoices.invoiceEmailMessage,
        s3Urls: invoices.invoiceEmailAttachments,
      }).unwrap()

      if (isError) {
        setErrorOnSending(true)
      } else {
        handleClose()
        dispatch(clearInvoiceEmail())
      }
    } catch (error: any) {
      setErrorOnSending(true)
      setErrorMessage(error.error)
    }
  }

  return {
    toEmail,
    userEmail,
    factoringEmails,
    errorOnSending,
    setErrorOnSending,
    errorMessage,
    emailSendLoading: isLoading,
    handleSendInvoice,
  }
}
